import { Stack, Typography, Divider, Box } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { docsBaseUrl } from '@/contexts/environment';
import type { BackupVault } from '@/data/vaults/backupVault';

import { KmsCopyablePolicy } from './kmsCopyablePolicy';
import { KmsEditKeyPolicyButton } from './kmsEditKeyPolicyButton';

import { BottomDialog } from '../dialogs/bottomDialog';
import { FormTextField } from '../form/formTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { PanelWrapper } from '../panels/panelWrapper';
import { ReadMoreLink } from '../shared/readMoreLink';

const KmsKeyDescription = () => {
  return (
    <Typography variant='body1' component={'span'}>
      Set the KMS key to encrypt snapshots with. You’re responsible for securing
      the key from unauthorized access and deletion. Without this key, snapshots
      can’t be retrieved from the vault.{' '}
      <ReadMoreLink
        href={`${docsBaseUrl}/user-guide/backing-up/vaults/about-customer-managed-vault-keys`}
      />
    </Typography>
  );
};

const kmsKeyArnConfig = getCloudFieldConfig(Provider.Aws, 'kmsKeyArn');

export const KMSKeyEditorPanel = ({
  open,
  onClose,
  data,
  setData,
}: {
  open: boolean;
  onClose: () => void;
  data: BackupVault;
  setData: (data: BackupVault) => void;
}) => {
  return (
    <BottomDialog open={open} isFullScreen={true}>
      <PanelWrapper
        header={{
          title: 'Set KMS Key',
          subtitle: <KmsKeyDescription />,
          fullWidthSubtitle: false,
          onClose: onClose,
        }}
        footer={{
          primaryButton: {
            text: 'Done',
            'data-testid': 'done-button',
            onClick: onClose,
          },
          secondaryButton: {
            text: 'Cancel',
            'data-testid': 'cancel-button',
            onClick: onClose,
          },
        }}
      >
        <Box padding='40px min(100px,6vw)' width='100%' height='auto'>
          <Stack
            gap='40px'
            divider={<Divider sx={{ borderStyle: 'dashed' }} />}
          >
            <NumericBulletSection bulletNumber={1} title='Set key details'>
              <BulletItem
                leftContent={
                  <FormTextField
                    title='KMS key ARN'
                    placeholder={kmsKeyArnConfig.placeholder}
                    value={data.vaultAttributes.aws?.encryptionKey ?? ''}
                    onChange={(event) => {
                      setData({
                        ...data,
                        vaultAttributes: {
                          ...data.vaultAttributes,
                          aws: {
                            ...data.vaultAttributes.aws,
                            encryptionKey: event.target.value,
                          },
                        },
                      });
                    }}
                    validation={kmsKeyArnConfig.validation}
                  />
                }
              />
            </NumericBulletSection>
            <NumericBulletSection bulletNumber={2} title='Add policy in AWS'>
              <BulletItem
                title='Review and add this policy in your AWS account. Afterward, come back to this tab to finish the connection.'
                leftContent={
                  <KmsEditKeyPolicyButton
                    kmsKey={data.vaultAttributes.aws?.encryptionKey}
                  />
                }
                rightContent={
                  <KmsCopyablePolicy
                    cloudProvider={data.vaultAttributes.cloudProvider}
                  />
                }
              />
            </NumericBulletSection>
          </Stack>
        </Box>
      </PanelWrapper>
    </BottomDialog>
  );
};
